import styled from 'styled-components'

const PRIMARY_SOFT_ALPHA = '1F'

export const HeaderTitle = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  font-size: ${({ theme }) => theme.fontSize.xl}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

export const HeaderTile = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ theme }) => theme.iconTile.size}rem;
  height: ${({ theme }) => theme.iconTile.size}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => `${theme.colors.primary}${PRIMARY_SOFT_ALPHA}`};
  color: ${({ theme }) => theme.colors.primary};

  /* ModalContainer's header styles every descendant svg as a clickable icon. */
  svg {
    width: ${({ theme }) => theme.fontSize.lg}rem;
    height: ${({ theme }) => theme.fontSize.lg}rem;
    padding: 0;
    cursor: default;
  }
`

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
`

export const Intro = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  text-wrap: pretty;
`

export const Eyebrow = styled.span`
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.iconGray};
`

export const ExamplePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.dashboard.surfaceSubtle};

  /* The three-column timeline has no readable form below the card breakpoint. */
  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    display: none;
  }
`

export const ExampleHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`

export const ExampleDates = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.2fr) minmax(0, 1.2fr);
  align-items: center;
  gap: 0 ${({ theme }) => theme.spacing.lg}rem;
  padding: 0 ${({ theme }) => theme.spacing.sm}rem;
`

export const ExampleDate = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem 0;

  strong {
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.neutralBlack};
  }

  span {
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
    color: ${({ theme }) => theme.dashboard.textSubtle};
  }
`

export const MethodRow = styled.div<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.2fr) minmax(0, 1.2fr);
  align-items: center;
  gap: 0 ${({ theme }) => theme.spacing.lg}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  cursor: pointer;
  transition: background 200ms, opacity 200ms, box-shadow 200ms;
  background: ${({ theme, $selected }) => ($selected ? theme.colors.neutralWhite : 'transparent')};
  box-shadow: ${({ theme, $selected }) => ($selected ? theme.shadow.soft : 'none')};
  opacity: ${({ $selected }) => ($selected ? 1 : 0.5)};
`

export const MethodLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

export const MethodNote = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
  text-wrap: pretty;
`

export const PillCell = styled.div`
  justify-self: start;
`

export const EntryPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => `${theme.spacing.xxs}rem ${theme.spacing.xs}rem`};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.dashboard.infoTint};
  color: ${({ theme }) => theme.dashboard.infoDeep};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: help;
  transition: background 200ms, color 200ms;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.neutralWhite};
  }
`

export const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  grid-auto-rows: auto;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

export const OptionCard = styled.div<{ $selected: boolean }>`
  position: relative;
  display: grid;
  grid-template-rows: subgrid;
  grid-row: span 3;
  align-content: start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 2px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.borderSoft)};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  cursor: pointer;
  transition: border-color 200ms;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  /* Stacked cards have nothing to align against, so they own their rows again. */
  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    grid-template-rows: none;
    grid-row: auto;
  }
`

export const OptionHeader = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};

  svg {
    flex-shrink: 0;
    width: ${({ theme }) => theme.fontSize.xl}rem;
    height: ${({ theme }) => theme.fontSize.xl}rem;
    color: ${({ theme, $selected }) =>
      $selected ? theme.colors.success : theme.colors.borderStrong};
  }
`

export const OptionDescription = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  text-wrap: pretty;
`

export const AutoEntries = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderSoft};
`

export const Bullet = styled.div<{ $muted?: boolean }>`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};

  svg {
    flex-shrink: 0;
    width: 12px;
    height: ${({ theme }) => theme.fontSize.px11}rem;
    color: ${({ theme, $muted }) => ($muted ? theme.colors.iconGray : theme.colors.primary)};
  }
`

export const RecommendationBadge = styled.div`
  position: absolute;
  top: -0.75rem;
  right: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

export const DocsLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.dashboard.infoDeep};
    text-decoration: underline;
  }

  svg {
    width: ${({ theme }) => theme.fontSize.px11}rem;
    height: ${({ theme }) => theme.fontSize.px11}rem;
  }
`

export const FooterActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
